/**
 * Managing shared folders (P1-F5): share, invite, members, activity, stop sharing, the invitee's
 * side of invitations, and the subscription's "Who can be invited" setting.
 *
 * Every call goes through {@link apiCallWithAuthCheck} like the other `/v1` calls. Errors are left
 * as Amplify throws them (`err.response.status`, `err.response.data.code`); turn them into text with
 * `shareErrorMessage` from `./errors`, never by showing `response.data.message` directly.
 */
import {API} from 'aws-amplify'
import {apiCallWithAuthCheck} from '@/app/modules/auth/core/AuthErrorInterceptor'
import type {
  EligibleMembersResponse,
  InviteTarget,
  MemberAccess,
  PendingShareInvitationsResponse,
  ShareView,
  ShareActivityPage,
  ShareInvitation,
  ShareInvitationPreview,
  ShareInvitationsResponse,
  ShareMembersResponse,
  SharingSettings,
} from './types'

const SHARING_API_NAME: string = process.env.REACT_APP_API_NAME || 'JobService'
const enc = encodeURIComponent

const get = <T>(path: string, queryStringParameters?: Record<string, string>): Promise<T> =>
  apiCallWithAuthCheck(() =>
    API.get(SHARING_API_NAME, path, queryStringParameters ? {queryStringParameters} : {})
  )

const post = <T>(path: string, body: object = {}): Promise<T> =>
  apiCallWithAuthCheck(() => API.post(SHARING_API_NAME, path, {body}))

const patch = <T>(path: string, body: object): Promise<T> =>
  apiCallWithAuthCheck(() => API.patch(SHARING_API_NAME, path, {body}))

const put = <T>(path: string, body: object): Promise<T> =>
  apiCallWithAuthCheck(() => API.put(SHARING_API_NAME, path, {body}))

const del = <T>(path: string): Promise<T> =>
  apiCallWithAuthCheck(() => API.del(SHARING_API_NAME, path, {}))

// --- Owner: the share itself ----------------------------------------------------------------

/** `folderPath` is relative to My Files and ends with `/`. */
export const createShare = (folderPath: string): Promise<ShareView> =>
  post('v1/shares', {folderPath})

export const getShare = (shareId: string): Promise<ShareView> => get(`v1/shares/${enc(shareId)}`)

/** Stop sharing. Immediate for every member; no files change. */
export const stopShare = (shareId: string): Promise<{shareId: string; status: 'stopped'}> =>
  del(`v1/shares/${enc(shareId)}`)

// --- Members ---------------------------------------------------------------------------------

export const listShareMembers = (shareId: string): Promise<ShareMembersResponse> =>
  get(`v1/shares/${enc(shareId)}/members`)

export const updateShareMember = (
  shareId: string,
  memberSub: string,
  access: MemberAccess
): Promise<{sub: string; access: MemberAccess}> =>
  patch(`v1/shares/${enc(shareId)}/members/${enc(memberSub)}`, {access})

/** Remove a member, or leave when `memberSub` is your own. */
export const removeShareMember = (
  shareId: string,
  memberSub: string
): Promise<{sub: string; status: 'removed'}> =>
  del(`v1/shares/${enc(shareId)}/members/${enc(memberSub)}`)

export const listEligibleMembers = (query: string): Promise<EligibleMembersResponse> =>
  get('v1/shares/eligible-members', query ? {query} : undefined)

// --- Invitations, owner side -----------------------------------------------------------------

export const listShareInvitations = (shareId: string): Promise<ShareInvitationsResponse> =>
  get(`v1/shares/${enc(shareId)}/invitations`)

export const inviteToShare = (
  shareId: string,
  target: InviteTarget,
  access: MemberAccess
): Promise<ShareInvitation> => post(`v1/shares/${enc(shareId)}/invitations`, {...target, access})

/** Withdraw a pending invitation. */
export const revokeShareInvitation = (
  shareId: string,
  inviteId: string
): Promise<{inviteId: string; status: 'revoked'}> =>
  del(`v1/shares/${enc(shareId)}/invitations/${enc(inviteId)}`)

// --- Invitations, invitee side ---------------------------------------------------------------

export const listMyShareInvitations = (): Promise<PendingShareInvitationsResponse> =>
  get('v1/share-invitations')

export const acceptShareInvitationById = (shareId: string, inviteId: string): Promise<ShareView> =>
  post(`v1/shares/${enc(shareId)}/invitations/${enc(inviteId)}/accept`)

export const declineShareInvitationById = (
  shareId: string,
  inviteId: string
): Promise<{inviteId: string; status: 'declined'}> =>
  post(`v1/shares/${enc(shareId)}/invitations/${enc(inviteId)}/decline`)

export const previewShareInvitation = (token: string): Promise<ShareInvitationPreview> =>
  get(`v1/share-invitations/${enc(token)}`)

export const acceptShareInvitation = (token: string): Promise<ShareView> =>
  post(`v1/share-invitations/${enc(token)}/accept`)

export const declineShareInvitation = (
  token: string
): Promise<{inviteId: string; status: 'declined'}> =>
  post(`v1/share-invitations/${enc(token)}/decline`)

// --- Activity --------------------------------------------------------------------------------

export const listShareActivity = (shareId: string, next?: string): Promise<ShareActivityPage> =>
  get(`v1/shares/${enc(shareId)}/activity`, next ? {next} : undefined)

// --- Subscription setting (owners and admins) ------------------------------------------------

export const getSharingSettings = (subscriptionId: string): Promise<SharingSettings> =>
  get(`v1/subscription/${enc(subscriptionId)}/sharing-settings`)

export const putSharingSettings = (
  subscriptionId: string,
  settings: SharingSettings
): Promise<SharingSettings> =>
  put(`v1/subscription/${enc(subscriptionId)}/sharing-settings`, {
    inviteScope: settings.inviteScope,
    allowedDomains: settings.allowedDomains,
    allowedSubscriptions: settings.allowedSubscriptions,
  })
